import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAppContext, ConsentOutcome } from '../../../Store/Store';

interface ConsentScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

// Consent config as served by GET /event-consents/
interface ConsentConfig {
  purpose: string;
  title: string;
  text: string;
  required: boolean;
  order: number;
  version: number;
}

const ConsentScreen: React.FC<ConsentScreenProps> = ({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}) => {
  const { backendBaseUrl, event, style, setConsents } = useAppContext();

  const [configs, setConfigs] = useState<ConsentConfig[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [loading, setLoading] = useState(true);
  // Whether a fetch against a valid event has actually completed. Guards the
  // auto-skip below so we don't skip before the event id is even available.
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    // Event id not ready yet — keep waiting, don't decide anything.
    if (!event?.id) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${backendBaseUrl}/event-consents/`, { params: { event_id: event.id } })
      .then(resp => {
        if (cancelled) return;
        const list: ConsentConfig[] = resp.data?.consents || [];
        setConfigs(list);
        setTitle(resp.data?.consent_title || '');
        setIntro(resp.data?.consent_intro || '');
        // Initialize every consent to "not granted".
        setChecked(
          list.reduce<Record<string, boolean>>((acc, c) => {
            acc[c.purpose] = false;
            return acc;
          }, {})
        );
      })
      .catch(err => {
        console.error('Failed to load event consents:', err);
        if (!cancelled) setConfigs([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setFetched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, event?.id]);

  // "Continue" unlocks only when every REQUIRED consent is granted.
  const allRequiredGranted = useMemo(
    () => configs.every(c => !c.required || checked[c.purpose]),
    [configs, checked]
  );

  const toggle = (purpose: string) => {
    setChecked(prev => ({ ...prev, [purpose]: !prev[purpose] }));
  };

  const publishOutcomes = () => {
    const outcomes: ConsentOutcome[] = configs.map(c => ({
      purpose: c.purpose,
      granted: Boolean(checked[c.purpose]),
      version: c.version,
    }));
    setConsents(outcomes);
  };

  const handleContinue = () => {
    if (!allRequiredGranted) return;
    publishOutcomes();
    onNext();
  };

  // Nothing configured for this event → behave like today: skip straight through
  // without showing anything and without recording any consent. Only after a real
  // fetch completed (fetched), never while the event id is still resolving.
  useEffect(() => {
    if (fetched && configs.length === 0) {
      // Skip silenzioso: da fuori si vede solo "consent non si apre e passa al
      // feedback". Logghiamo il perche', perche' e' quasi sempre una config
      // mancante — ConsentType e' per-Event (nessun aggancio a EventType), quindi
      // l'ordine screen sull'event type non porta con se' i consensi.
      console.warn(
        `⚠️ Consent screen skipped: no active ConsentType for event ${event?.id}. ` +
          'Configure ConsentType rows (active=true) on THIS event.'
      );
      setConsents([]);
      onNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetched, configs.length]);

  if (loading || !fetched) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          boxSizing: 'border-box',
        }}
      >
        Loading...
      </div>
    );
  }

  if (configs.length === 0) {
    // The effect above is advancing; render nothing meanwhile.
    return <div style={{ display: 'none' }} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '1.25rem',
        boxSizing: 'border-box',
        gap: '1rem',
        overflowY: 'auto',
      }}
    >
      {title && (
        <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>{title}</h2>
      )}
      {intro && (
        <p
          style={{ margin: 0, lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: intro }}
        />
      )}

      {configs.map(c => (
        <label
          key={c.purpose}
          htmlFor={`consent-${c.purpose}`}
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'flex-start',
            cursor: 'pointer',
            lineHeight: 1.4,
          }}
        >
          <input
            id={`consent-${c.purpose}`}
            type="checkbox"
            checked={Boolean(checked[c.purpose])}
            onChange={() => toggle(c.purpose)}
            style={{ marginTop: '0.25rem', width: 20, height: 20, flexShrink: 0 }}
          />
          <span>
            {c.title && (
              <strong style={{ display: 'block' }}>
                {c.title}
                {c.required ? ' *' : ''}
              </strong>
            )}
            <span dangerouslySetInnerHTML={{ __html: c.text }} />
          </span>
        </label>
      ))}

      <button
        type="button"
        onClick={handleContinue}
        disabled={!allRequiredGranted}
        style={{
          height: '4.6vh',
          minHeight: '44px',
          width: 'min(34vh, 90vw)',
          maxWidth: '420px',
          alignSelf: 'center',
          borderRadius: '7px',
          padding: '0 20px',
          fontSize: '90%',
          fontWeight: 'bold',
          color: style?.text_review_button_text_color || '#07296A',
          background: allRequiredGranted
            ? style?.text_review_button_bg_color ||
              'linear-gradient(to right, #6991F8 0%, #07296a 100%)'
            : '#c7c9cf',
          border: 'none',
          cursor: allRequiredGranted ? 'pointer' : 'not-allowed',
          boxShadow:
            '0 5px 5px ' +
            (style?.text_review_button_shadow_color || '#999'),
        }}
        dangerouslySetInnerHTML={{
          __html: style?.text_review_send_button_indication || 'Continue',
        }}
      />

      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}

      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
};

export default ConsentScreen;
